import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import "./Storage.css";

import { useSimulationData } from "../../../../hooks/useSimulationData";
import storageWorkerService from "../../../../services/storageWorkerService";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import {
  buildTwinStrokeData,
  isDrivingPoint,
  readTwinStrokePoint,
} from "../../utils/twinStrokeSeries";
import { parseSimulationDate } from "../../utils/simulationDate";
import { makeStorageFlowReader } from "../../utils/columnLookup";
import { getDeviceColor, BESS_BASE_COLOR } from "../../utils/deviceColors";
import { formatWithCommas } from "../../utils/formatNumber";
import {
  buildDataZoom,
  darkTooltip,
  formatDate,
  getXAxisTitle,
  HOURLY_BUCKET_STEP,
} from "../../utils/chartOptions";

const STORAGE_SUMMARY_SERIES_NAME = "Storage Summary";

/* Same key the stat cards build in public/workers/metrics.worker.js. */
const drivingKeyFor = (deviceName) => `${deviceName} EV Driving Energy (kWh)`;

/* The same formatter StorageChartPage's stat cards use, so a value read off
 * the tooltip and the same value read off the card directly above it now render
 * identically. The private formatter this replaces differed twice over: it
 * dropped a trailing zero (1,234.5 against the card's 1,234.50) and it followed
 * the browser locale where the cards are pinned to en-US, so on a de-DE machine
 * the chart said 1.234,5 while the card said 1,234.50. */
const formatEnergy = (value) => formatWithCommas(value);

export default function StorageChart({
  csvData,
  bessMaxCapacity,
  evMaxCapacity,
  deviceNames,
  onLoadingStateChange,
}) {
  /* ── data from CSV ─────────────────────── */
  const sim = useSimulationData(csvData) || {};
  const dailyData = Array.isArray(sim.dailyData) ? sim.dailyData : [];
  const isLoading = Boolean(sim.isLoading);

  /* ── exported data context ─────────────── */
  const { setExportedStorageData } = useExportedData();

  /* ── UI state ──────────────────────────── */
  const [view, setView] = useState("yearly");
  const [selDate, setSelDate] = useState("");
  const [selMonth, setSelMonth] = useState("");
  const [selYear, setSelYear] = useState("");
  const [chartData, setChartData] = useState([]);
  const formatSelectedPeriodType = () => {
    if (view === "daily") {
      return "Day";
    } else if (view === "monthly") {
      return "Month";
    } else if (view === "yearly") {
      return "Year";
    } else if (view === "all time view") {
      return "All Time View";
    }
    return "";
  };
  const formatSelectedPeriod = () => {
    if (view === "daily") {
      const [year, month, day] = selDate.split("-");
      return `${month}/${day}/${year}`; // MM/DD/YYYY
    } else if (view === "monthly") {
      const [year, month] = selMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${year}`;
    } else if (view === "yearly") {
      return selYear;
    } else if (view === "all time view") {
      return "";
    }
    return "";
  };

  
  const capLookup = useMemo(() => {
  const caps = {};

  Object.entries(bessMaxCapacity || {}).forEach(([type, values]) => {
    (values || []).forEach((val, i) => {
      const name = deviceNames?.[type]?.[i];
      if (name) caps[name] = +val || 0;
    });
  });

  Object.entries(evMaxCapacity || {}).forEach(([type, values]) => {
    (values || []).forEach((val, i) => {
      const name = deviceNames?.[type]?.[i];
      if (name) caps[name] = +val || 0;
    });
  });

  return caps;
}, [deviceNames, bessMaxCapacity, evMaxCapacity]);

  const safeDeviceNames = useMemo(
    () => ({
      BESS: Array.isArray(deviceNames?.BESS) ? deviceNames.BESS : [],
      EV: Array.isArray(deviceNames?.EV) ? deviceNames.EV : [],
    }),
    [deviceNames],
  );

  /* The Date column reaches here as whatever string the result file carried —
   * "01/01/2040 00:00" from the sim, ISO from other writers — so every period
   * key below comes from a parse rather than a fixed offset into the stamp.
   * One parse per row, positionally aligned with dailyData. */
  const periodKeys = useMemo(
    () => dailyData.map((row) => parseSimulationDate(row?.Date)),
    [dailyData],
  );

  /* First and last stamp the result actually covers. The native pickers below
   * reject a min/max they cannot read as ISO, which these keys always are. */
  const [firstKey, lastKey] = useMemo(() => {
    const readable = periodKeys.filter(Boolean);
    return [readable[0] ?? null, readable[readable.length - 1] ?? null];
  }, [periodKeys]);

  const availableYears = useMemo(
    () => Array.from(new Set(periodKeys.filter(Boolean).map((key) => key.year))),
    [periodKeys],
  );

  /* ── initialise selectors once ─────────── */
  useEffect(() => {
    if (!firstKey) return;
    setSelDate(firstKey.day);
    setSelMonth(firstKey.month);
    setSelYear(firstKey.year);
  }, [firstKey]);

  /* Falls back to the first period the result covers rather than slicing on an
   * empty selector. The selectors are filled by an effect, so for one render
   * after a view switch the new view's selector is still "" and every row is
   * filtered out - which unmounts ReactECharts, disposing the instance and
   * taking the user's zoom range and legend selection with it. Same reasoning,
   * and the same three lines, in the other five period charts. */
  const activeDay = selDate || firstKey?.day || "";
  const activeMonth = selMonth || firstKey?.month || "";
  const activeYear = selYear || firstKey?.year || "";

  /* ── slice rows whenever view changes ──── */
  useEffect(() => {
    if (!dailyData.length) return;
    let rows = [];
    if (view === "daily")
      rows = dailyData.filter((_, i) => periodKeys[i]?.day === activeDay);
    if (view === "monthly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.month === activeMonth);
    if (view === "yearly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.year === activeYear);
    if (view === "all time view") rows = dailyData;
    setChartData(rows);
  }, [view, activeDay, activeMonth, activeYear, dailyData, periodKeys]);

  const xAxisTitle = getXAxisTitle(view);

  /* ── READY? call the worker ────────────── */
  const hasDevices =
    (safeDeviceNames.BESS.length ?? 0) + (safeDeviceNames.EV.length ?? 0) > 0;
  
  useEffect(() => {
    if (!chartData.length || !hasDevices) return; // ← wait until both ready

    storageWorkerService
      .run({
        type: "CALCULATE_PER_DEVICE_LIST",
        data: { chartData, deviceNames: safeDeviceNames, maxCapacities: capLookup },
      })
      .then((list) => {
        const stamped = list.map((m) => ({
          ...m,
          view: formatSelectedPeriodType(),
          selectedPeriod: formatSelectedPeriod(),
          /* The period the card is describing, as ISO KEYS rather than the
           * display strings beside them.
           *
           * "January 2040" is a label; a card that has to slice the daily
           * summary by the same period cannot use it without reverse-parsing a
           * locale string back into "2040-01", which is a guess that changes
           * with the browser's locale. These are the exact keys
           * `sumDemandChargeForPeriod` and `sumDeviceAttributionForPeriod`
           * filter on -- startsWith / slice(0,7) / slice(0,4) -- and
           * `periodView` is the raw view token, not the "Day"/"Month"/"Year"
           * label above it.
           *
           * Stamped from the ACTIVE keys, not the selector state: the selectors
           * are filled by an effect, so for one render after a view switch the
           * new view's selector is still "" while the rows below were already
           * sliced by the fallback. Stamping the selector would label the card
           * with a period the figures on it do not cover. */
          periodView: view,
          selDate: activeDay,
          selMonth: activeMonth,
          selYear: activeYear,
        }));
        setExportedStorageData(stamped);
        onLoadingStateChange?.(false);
      })
      .catch(console.error);
  }, [
    chartData,
    hasDevices,
    view,
    selDate,
    selMonth,
    selYear,
    activeDay,
    activeMonth,
    activeYear,
    safeDeviceNames,
    capLookup,
  ]);

  /* ── notify parent while loading ───────── */
  useEffect(() => {
    onLoadingStateChange?.(isLoading || !chartData.length || !hasDevices);
  }, [isLoading, chartData.length, hasDevices]);

  /* A BESS device's columns are named "<dev> Battery <verb>" by some writers
   * and "<dev> BESS <verb>" by others. This used to hardcode BOTH at once -
   * "Battery" for the split columns and "BESS" for the combined one - so a
   * result using either of the other two combinations plotted a flat zero while
   * the stat cards above it, which probe the header, reported real energy for
   * the same run. makeStorageFlowReader makes the same probe the cards make.
   * See utils/columnLookup.js. */
  const readStorageFlow = useMemo(() => {
    const readers = new Map();
    safeDeviceNames.BESS.forEach((name) => {
      readers.set(name, makeStorageFlowReader(chartData, name, "BESS"));
    });
    safeDeviceNames.EV.forEach((name) => {
      readers.set(name, makeStorageFlowReader(chartData, name, "EV"));
    });
    return (entry, name) => readers.get(name)?.(entry) ?? 0;
  }, [chartData, safeDeviceNames]);

  /* Energy that left the EV through the wheels rather than the plug. Absent
   * from results produced before the sim's two-exit accounting change, and
   * from BESS devices, which have no wheels exit — both read as 0 here. */
  const getEVDriving = (entry, name) => Number(entry[drivingKeyFor(name)]) || 0;

  /* Axis tooltip. A driving sample sits on the positive axis alongside plug
   * discharge, so the label is what tells them apart — "Discharge: 550" on a
   * driving segment is the exact confusion this feature exists to prevent. */
  const tooltipFormatter = useCallback((params) => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return "";
    const lines = [];
    items.forEach((p) => {
      const point = readTwinStrokePoint(p.data, p.value);
      if (!point) return;
      const label = isDrivingPoint(point)
        ? `${p.seriesName} (Driving)`
        : p.seriesName;
      lines.push(`${p.marker}${label}: ${formatEnergy(point.value)}`);
    });
    if (!lines.length) return "";
    return [items[0].axisValueLabel ?? items[0].name, ...lines].join("<br/>");
  }, []);

  // Optimized ECharts configuration
  const echartOptions = useMemo(
    () => {
      const bessValues = safeDeviceNames.BESS.map((name) =>
        chartData.map((entry) => readStorageFlow(entry, name) || 0),
      );
      /* A colour per device rather than per category: two devices of the same
       * kind used to share one colour, and a storage line sits on zero for
       * every idle interval, so their strokes lay on top of each other with
       * nothing to tell them apart. Same helper and same first colour as
       * Summary/OverlayChart.jsx. See utils/deviceColors.js. */
      const bessSeries = safeDeviceNames.BESS.map((name, index) => ({
        name,
        type: "line",
        step: HOURLY_BUCKET_STEP,
        data: bessValues[index],
        itemStyle: { color: getDeviceColor(BESS_BASE_COLOR, index) },
        symbol: "circle",
        symbolSize: 4,
        lineStyle: { width: 2 },
      }));

      /* Each EV becomes two series under one name: a solid one holding plug
       * flow and a dashed one holding driving, each null where the other is
       * drawn, and joined at every transition so the line never visibly
       * breaks. ECharts cannot vary lineStyle per point within a series, and
       * the shared name makes the pair a single legend entry that toggles
       * together. The split, the sign convention and the join selection all
       * live in utils/twinStrokeSeries.js, which Summary/OverlayChart.jsx
       * calls for the same devices. */
      const evTwins = safeDeviceNames.EV.map((name) => ({
        name,
        ...buildTwinStrokeData(
          chartData.map((entry) => readStorageFlow(entry, name) || 0),
          chartData.map((entry) => getEVDriving(entry, name)),
        ),
      }));

      const evSeries = evTwins.flatMap(
        ({ name, solid, dashed, connectors, hasDriving }, index) => {
        /* Everything the device's series have in common. The connectors below
         * take this WITHOUT `step`: they are explicit [column, value] pairs
         * drawing a vertical of no width at a hand-over, and stepping them
         * would give them an interval to paint - which is the whole thing they
         * exist to avoid. */
        const base = {
          name,
          /* Every series of one device shares its colour, so the set still
           * reads as the single line it is. */
          itemStyle: { color: getDeviceColor(null, index, "EV") },
          symbol: "circle",
          symbolSize: 4,
        };
        const shared = { ...base, type: "line", step: HOURLY_BUCKET_STEP };

        const series = [
          {
            ...shared,
            data: solid,
            lineStyle: { width: 2, type: "solid" },
          },
        ];
        /* A device that never drove in this period — a legacy result with no
         * driving column, or simply a vehicle that stayed put — gets no dashed
         * twin at all, so its line is 100% solid like a battery. */
        if (hasDriving) {
          series.push(
            {
              ...shared,
              data: dashed,
              lineStyle: { width: 2, type: "dashed" },
            },
            /* The hand-over verticals - see utils/twinStrokeSeries.js. */
            {
              ...base,
              type: "line",
              data: connectors.solid,
              symbol: "none",
              lineStyle: { width: 2, type: "solid" },
            },
            {
              ...base,
              type: "line",
              data: connectors.dashed,
              symbol: "none",
              lineStyle: { width: 2, type: "dashed" },
            },
          );
        }
        return series;
      },
      );

      const storageDeviceSeries = [...bessSeries, ...evSeries];
      /* Summed from the per-device values rather than the rendered series, so
       * the twin split and its join samples cannot double-count. */
      const deviceValues = [
        ...bessValues,
        ...evTwins.map((twin) => twin.values),
      ];
      const storageSummarySeries =
        storageDeviceSeries.length > 0
          ? chartData.map((_, index) =>
              deviceValues.reduce(
                (sum, values) => sum + (Number(values[index]) || 0),
                0,
              ),
            )
          : [];

      return {
        animation: true,
        animationDuration: 1000,
        animationEasing: "cubicInOut",
        tooltip: darkTooltip({ formatter: tooltipFormatter }),
        legend: {
          show: true,
          data: [
            ...(storageDeviceSeries.length > 0
              ? [STORAGE_SUMMARY_SERIES_NAME]
              : []),
            ...safeDeviceNames.BESS,
            ...safeDeviceNames.EV,
          ],
          top: 0,
          right: 10,
          textStyle: {
            color: "#666",
            fontSize: 10,
          },
        },
        dataZoom: buildDataZoom({
          accent: "#4CAF50",
          fillerColor: "rgba(76, 175, 80, 0.2)",
        }),
        grid: {
          left: "2%",
          right: "0.5%",
          top: "40px",
          bottom: "80px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: chartData.map((entry) => formatDate(entry.Date)),
          name: xAxisTitle,
          nameLocation: "middle",
          nameGap: 40,
          axisLine: {
            lineStyle: {
              color: "#999",
              width: 1,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "Energy (kWh)",
          nameLocation: "middle",
          nameGap: 40,
          axisLine: {
            lineStyle: {
              color: "#999",
              width: 1,
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#ddd",
            },
          },
        },
        series: [
          ...(storageSummarySeries.length > 0
            ? [
                {
                  name: STORAGE_SUMMARY_SERIES_NAME,
                  type: "line",
                  /* The sum of step functions is a step function. If the
                   * aggregate curved while its own parts stepped, a reader
                   * checking the total against the devices under it would see
                   * the two disagree everywhere except at the hour marks. */
                  step: HOURLY_BUCKET_STEP,
                  data: storageSummarySeries,
                  itemStyle: { color: "#1B7F5A" },
                  lineStyle: { width: 3, color: "#1B7F5A" },
                  symbol: "none",
                  z: 5,
                },
              ]
            : []),
          ...storageDeviceSeries,
        ],
      };
    },
    [chartData, safeDeviceNames, readStorageFlow, xAxisTitle, tooltipFormatter, view],
  );

  /* An EV contributes one series or two depending on whether it drove in the
   * period, so the series array changes length and every device after the one
   * that changed shifts slot. ECharts' default merge matches series by array
   * position, and leaves any series the new option has no slot for standing on
   * the canvas — a stale stroke drawn under the live one, which reads as a
   * duplicated line as soon as the chart is zoomed far enough for the two to
   * separate. replaceMerge rebuilds the series array instead, and unlike
   * notMerge it leaves the rest of the chart alone: the same instance, and the
   * legend selection the user set, survive the update. Same reasoning, and the
   * same tooltip guard, as Summary/OverlayChart.jsx. */
  const chartRef = useRef(null);
  // Series identity as of the last setOption. The two strokes of one EV share
  // a name for the legend, so the stroke has to be part of the signature.
  const previousSeriesKeysRef = useRef(null);
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (!chartInstance) return;

    /* ECharts replays the tooltip payload it captured before the update, and
     * replaceMerge renumbers the series, so a replayed payload can name an
     * index that no longer resolves. hideTip drops that payload. Only needed
     * when the series set actually changed. */
    const seriesKeys = (echartOptions.series || []).map(
      (entry) => `${entry?.name}|${entry?.lineStyle?.type ?? ""}`,
    );
    const previousKeys = previousSeriesKeysRef.current;
    const seriesSetChanged =
      !previousKeys ||
      previousKeys.length !== seriesKeys.length ||
      seriesKeys.some((key, index) => key !== previousKeys[index]);
    previousSeriesKeysRef.current = seriesKeys;

    if (seriesSetChanged) {
      chartInstance.dispatchAction({ type: "hideTip" });
    }

    /* A series-set change must be applied synchronously. lazyUpdate defers the
     * render but NOT the model: setOption returns with the new, shorter series
     * array already in place - and replaceMerge leaves a hole where a removed
     * series was, because ECharts keeps component slots by id. Any axis-pointer
     * activity in that window (the mouse is still over the chart) collects
     * against the stale coordinate system and hands the tooltip series indices
     * that no longer resolve. getSeriesByIndex is a raw array index - alone
     * among ECharts' series accessors it does not filter holes, unlike
     * getSeriesByName/getSeriesByType - so it returns undefined and
     * TooltipView._showAxisTooltip crashes on undefined.getDataParams.
     *
     * hideTip above cannot cover this: it clears the payload captured BEFORE
     * the update, while this one is collected after it. Closing the window is
     * the fix. Data-only updates - zoom, hover highlighting - keep the batching,
     * since nothing is removed and no index can go stale. */
    chartInstance.setOption(echartOptions, {
      replaceMerge: ["series"],
      lazyUpdate: !seriesSetChanged,
    });
  }, [echartOptions]);

  // The effect above owns every update after the first, so the declarative
  // path must not also fire one with the default index merge.
  const suppressDeclarativeSetOption = useCallback(() => false, []);

  // Handle date changes
  const handleDateChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (view === "daily") setSelDate(value);
      else if (view === "monthly") setSelMonth(value);
      else if (view === "yearly") setSelYear(value);
    },
    [view],
  );

  if (isLoading || !dailyData.length || !hasDevices)
    return (
      <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
        <Skeleton height={350} />
      </SkeletonTheme>
    );

  return (
    <div className="chart-container-storage">
      <h3>Storage History</h3>
      <div className="controls-storage">
        <select
          onChange={(e) => setView(e.target.value)}
          value={view}
          style={{ marginRight: "10px", cursor: "pointer" }}
        >
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="all time view">All Time View</option>
        </select>

        {view === "daily" && (
          <input
            type="date"
            onChange={handleDateChange}
            value={selDate}
            min={firstKey?.day || ""}
            max={lastKey?.day || ""}
          />
        )}
        {view === "monthly" && (
          <input
            type="month"
            onChange={handleDateChange}
            value={selMonth}
            min={firstKey?.month || ""}
            max={lastKey?.month || ""}
          />
        )}

        {view === "yearly" && (
          <select onChange={handleDateChange} value={selYear}>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="chart-wrapper" style={{ height: 600 }}>
        {chartData.length > 0 ? (
          <ReactECharts
            ref={chartRef}
            option={echartOptions}
            shouldSetOption={suppressDeclarativeSetOption}
            style={{ width: "100%", height: "100%" }}
            lazyUpdate={true}
            opts={{ renderer: "canvas" }}
          />
        ) : (
          <div className="no-data-message">
            No data available for selected period
          </div>
        )}
      </div>
    </div>
  );
}
